"use client";

import React, { useState, useEffect } from 'react';
import { useTheme } from '@/components/ThemeContext';
import { 
  Layers, Wallet, TableProperties, TrendingUp, TrendingDown, 
  ChevronRight, ArrowUpRight, Search, SlidersHorizontal, Download, 
  Building2, X, FileText, Phone, Mail, User as UserIcon, AlertCircle
} from 'lucide-react';
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { motion, AnimatePresence } from 'framer-motion';
import SnapPayAIEstimateDemo from '@/components/pay-agent/SnapPayAIEstimateDemo';
import ProjectsInsightTracker from '@/components/ProjectsInsightTracker';
import FinancePanel from '@/components/FinancePanel';
import { useProjects, useKpi, useAging } from '@/features/snap-pay/api/client';
import { mutate } from 'swr';

// --- Types & Constants ---
type TabType = 'estimate' | 'pipeline' | 'finance' | 'insight';

const CASH_FLOW_DATA = [
  { name: '10월', in: 18000, out: 9500 },
  { name: '11월', in: 24000, out: 11000 },
  { name: '12월', in: 31000, out: 13000 },
  { name: '1월', in: 15000, out: 8500 },
  { name: '2월', in: 42000, out: 16000 },
  { name: '3월', in: 38000, out: 14000 },
  { name: '4월', in: 41000, out: 23000 },
];

const EXPENSE_DATA = [
  { name: '인건비', value: 445461291, color: '#10b981' },
  { name: '임대료/관리비', value: 180000000, color: '#3b82f6' },
  { name: '법인카드', value: 125000000, color: '#f59e0b' },
  { name: '외주용역', value: 95000000, color: '#8b5cf6' },
  { name: '기타/공과금', value: 108338709, color: '#94a3b8' },
];

const STATUS: Record<string, any> = {
  paid_done:     { label: '납품완료 · 입금완료', dot: 'bg-emerald-500', cls: 'text-emerald-700 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-500/10' },
  billing:       { label: '작업완료 · 청구대기', dot: 'bg-amber-500',   cls: 'text-amber-700 bg-amber-50 dark:text-amber-300 dark:bg-amber-500/10' },
  in_progress:   { label: '작업진행 · 청구대기', dot: 'bg-blue-500',    cls: 'text-blue-700 bg-blue-50 dark:text-blue-300 dark:bg-blue-500/10' },
  dispute:       { label: '미수금 · 독촉',      dot: 'bg-rose-500',    cls: 'text-rose-700 bg-rose-50 dark:text-rose-300 dark:bg-rose-500/10' },
  lost:          { label: '타사수주 (Lost)',    dot: 'bg-slate-400',   cls: 'text-slate-500 bg-slate-100 dark:text-slate-400 dark:bg-white/[0.04]' },
  QUOTE:         { label: '견적 · 제안',        dot: 'bg-slate-400',   cls: 'text-slate-700 bg-slate-100 dark:text-slate-300 dark:bg-slate-800/50' },
  IN_PROGRESS:   { label: '작업진행',           dot: 'bg-blue-500',    cls: 'text-blue-700 bg-blue-50 dark:text-blue-300 dark:bg-blue-500/10' },
  DONE:          { label: '작업완료',           dot: 'bg-emerald-500', cls: 'text-emerald-700 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-500/10' },
  BILLING:       { label: '청구진행',           dot: 'bg-amber-500',   cls: 'text-amber-700 bg-amber-50 dark:text-amber-300 dark:bg-amber-500/10' },
  PAID:          { label: '수금완료',           dot: 'bg-indigo-500',  cls: 'text-indigo-700 bg-indigo-50 dark:text-indigo-300 dark:bg-indigo-500/10' },
};

function TrendPill({ delta, isUp }: { delta: string, isUp: boolean }) {
  if (isUp) return <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-bold tabular-nums tracking-tight"><ArrowUpRight className="w-3 h-3"/> {delta}</span>;
  return <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 font-bold tabular-nums tracking-tight">{delta}</span>;
}

function StatusBadge({ status }: { status: string }) {
  const s = STATUS[status] || STATUS.QUOTE;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg text-xs font-semibold ${s.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

function ProgressBar({ value, total }: { value: number, total: number }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3 min-w-[120px]">
      <div className="h-1.5 flex-1 rounded-full bg-slate-100 dark:bg-white/[0.06] overflow-hidden">
        <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-emerald-400 transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-mono font-semibold tabular-nums text-slate-500 w-8 text-right">{pct}%</span>
    </div>
  );
}

const fmt = (n: number) => n.toLocaleString();

function ErrorCard({ msg }: { msg: string }) {
  return (
    <div className="p-6 rounded-2xl bg-rose-50 dark:bg-rose-900/10 border border-rose-100 dark:border-rose-800 text-rose-700 dark:text-rose-400 m-8">
      <h2 className="font-bold mb-2">Error Loading Data</h2>
      <p className="text-sm font-mono">{msg}</p>
    </div>
  );
}

export default function SnapPayDashboardPage() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  const [activeTab, setActiveTab] = useState<TabType>('pipeline');
  const [mounted, setMounted] = useState(false);
  const [selectedProject, setSelectedProject] = useState<any | null>(null);
  const [drawerTab, setDrawerTab] = useState<'overview' | 'timeline' | 'communication' | 'docs'>('overview');
  const [toastMsg, setToastMsg] = useState('');

  const showToast = (msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(''), 3000);
  };

  const { data: kpiData, error: kpiErr, isLoading: kpiLd } = useKpi();
  const { data: projRes, error: projErr, isLoading: projLd } = useProjects({ limit: '10' });
  const { data: agingData, error: agingErr, isLoading: agingLd } = useAging();

  useEffect(() => {
    setMounted(true);
    // document.body.style.backgroundColor = isDark ? '#05070D' : '#f8fafc';
  }, [isDark]);

  if (!mounted) return null;
  if (kpiErr || projErr || agingErr) return <ErrorCard msg={String(kpiErr?.message || projErr?.message || agingErr?.message)} />;

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#05070D]' : 'bg-slate-50'} transition-colors`}>
      {activeTab !== 'estimate' && (
        <div className="px-4 sm:px-6 lg:px-8 xl:px-10 py-6 lg:py-8 w-full space-y-6 lg:space-y-8 text-slate-900 dark:text-slate-200">
          
          <header className="mb-2 flex flex-col gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold tracking-tight text-slate-900 dark:text-white flex items-center gap-3">
                <span aria-hidden="true">💳</span> SnapPay 파이프라인 관제
              </h1>
              <p className="mt-1.5 text-sm text-slate-500 dark:text-slate-400 font-medium">전역 프로젝트 수금 현황 및 송장 발행 통합 워크벤치</p>
            </div>
          </header>

          {/* Underline Tabs */}
          <div className="flex border-b border-slate-200 dark:border-white/10 gap-6 overflow-x-auto no-scrollbar">
            {[
              { id: 'estimate', label: '✨ AI 견적 엔진' },
              { id: 'pipeline', label: '프로젝트 관제' },
              { id: 'finance', label: '전사 재무 흐름' },
              { id: 'insight', label: '미수금 관제' }
            ].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id as TabType)}
                className={`pb-3 text-sm font-bold transition-all whitespace-nowrap relative ${
                  activeTab === tab.id 
                    ? 'text-blue-600 dark:text-white' 
                    : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
                }`}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <motion.div layoutId="activeTabUnderline" className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-white" />
                )}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            {activeTab === 'pipeline' && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-6 lg:space-y-8">
                
                {/* 1. KPI Hero */}
                <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
                  {kpiLd ? (
                    Array(4).fill(0).map((_,i) => <div key={i} className="h-32 rounded-2xl bg-slate-200 dark:bg-white/5 animate-pulse border border-slate-100 dark:border-white/5" />)
                  ) : (
                    (() => {
                      const KPI_LIST = [
                        { label: '당월 총 계약/매출', value: fmt(Math.round((kpiData?.data?.totalRevenue ?? 0) / 10000)), unit: '만원', delta: `${kpiData?.data?.revenueTrend ?? 0}%`, isUp: (kpiData?.data?.revenueTrend ?? 0) >= 0, bar: 'from-indigo-500 to-purple-600', icon: Wallet },
                        { label: '총 미수금 잔액', value: fmt(Math.round((kpiData?.data?.outstandingAmount ?? 0) / 10000)), unit: '만원', delta: `${kpiData?.data?.outstandingTrend ?? 0}%`, isUp: (kpiData?.data?.outstandingTrend ?? 0) <= 0, bar: 'from-rose-500 to-rose-600', icon: AlertCircle },
                        { label: '누적 회수율', value: kpiData?.data?.collectionRate?.toFixed(1) ?? '0.0', unit: '%', delta: `${kpiData?.data?.collectionTrend ?? 0}%p`, isUp: (kpiData?.data?.collectionTrend ?? 0) >= 0, bar: 'from-emerald-400 to-emerald-600', icon: TrendingUp },
                        { label: '평균 회수일 (DSO)', value: fmt(kpiData?.data?.avgCollectionDays ?? 0), unit: '일', delta: `${kpiData?.data?.collectionDaysTrend ?? 0}일`, isUp: (kpiData?.data?.collectionDaysTrend ?? 0) <= 0, bar: 'from-blue-500 to-indigo-500', icon: Layers },
                      ];
                      return KPI_LIST.map((k) => (
                        <article key={k.label} className="group relative overflow-hidden rounded-2xl p-5 lg:p-6 border border-slate-200/70 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] dark:border-white/[0.06] dark:bg-slate-900/60 transition-all hover:shadow-[0_8px_24px_-12px_rgba(15,23,42,0.18)]">
                          <div className="flex items-center justify-between text-xs font-semibold text-slate-500 dark:text-slate-400">
                            <span className="uppercase tracking-wide">{k.label}</span>
                            <k.icon className="w-4 h-4 opacity-70" />
                          </div>
                          <div className="mt-3 flex items-baseline gap-1.5">
                            <span className="text-2xl lg:text-3xl font-semibold tracking-tight font-mono tabular-nums text-slate-900 dark:text-white">{k.value}</span>
                            <span className="text-sm font-medium text-slate-400">{k.unit}</span>
                          </div>
                          <div className="mt-2.5 flex items-center gap-2">
                            <TrendPill delta={k.delta} isUp={k.isUp} />
                            <span className="text-xs font-medium text-slate-400">vs 지난 달</span>
                          </div>
                          <span className={`absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r ${k.bar}`} />
                        </article>
                      ));
                    })()
                  )}
                </section>

                {/* 2. Cash Flow & Expense */}
                <section className="grid grid-cols-1 xl:grid-cols-[1.4fr_1fr] gap-5">
                  {/* Cash Flow */}
                  <article className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)] dark:border-white/[0.06] dark:bg-slate-900/60 dark:shadow-none">
                    <header className="flex justify-between items-center mb-6">
                      <div>
                         <h3 className="text-[15px] font-bold text-slate-900 dark:text-white tracking-tight">현금 흐름 스트림</h3>
                         <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mt-1">월별 입출금 추이</p>
                      </div>
                      <div className="flex bg-slate-100 dark:bg-white/[0.04] p-1 rounded-lg">
                        {['1M','3M','YTD','12M'].map(p => (
                          <button key={p} className={`px-3 py-1 text-xs font-bold rounded ${p==='12M' ? 'bg-white dark:bg-slate-800 shadow-sm text-slate-900 dark:text-white' : 'text-slate-500 dark:text-slate-400'}`}>{p}</button>
                        ))}
                      </div>
                    </header>
                    <div className="h-[240px] w-full min-w-0">
                      <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                        <AreaChart data={CASH_FLOW_DATA} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
                          <defs>
                            <linearGradient id="colorIn" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#38bdf8" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#38bdf8" stopOpacity={0}/>
                            </linearGradient>
                            <linearGradient id="colorOut" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#f43f5e" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: isDark ? '#94a3b8' : '#64748b' }} dy={10} />
                          <Tooltip cursor={{ stroke: isDark ? '#334155' : '#e2e8f0', strokeWidth: 1, strokeDasharray: '4 4' }}
                            contentStyle={{ backgroundColor: isDark ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(8px)', borderRadius: '12px', border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', padding: '12px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}
                            itemStyle={{ fontSize: 13, fontWeight: 700, fontFamily: 'monospace' }}
                          />
                          <Area type="monotone" dataKey="in" name="입금" stroke="#38bdf8" strokeWidth={2} fillOpacity={1} fill="url(#colorIn)" />
                          <Area type="monotone" dataKey="out" name="지출" stroke="#f43f5e" strokeWidth={2} fillOpacity={1} fill="url(#colorOut)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </article>

                  {/* Expense Breakdown */}
                  <article className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)] dark:border-white/[0.06] dark:bg-slate-900/60 dark:shadow-none flex flex-col">
                    <header className="mb-4">
                       <h3 className="text-[15px] font-bold text-slate-900 dark:text-white tracking-tight">지출 구조 Top 5</h3>
                       <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mt-1">당해년도 누적 (만원)</p>
                    </header>
                    <ul className="flex-1 space-y-4 justify-center flex flex-col">
                      {EXPENSE_DATA.map(d => {
                        const pct = Math.round((d.value / 953800000) * 100);
                        return (
                          <li key={d.name} className="flex flex-col gap-1.5">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: d.color }} />
                                <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">{d.name}</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="text-sm font-semibold font-mono tabular-nums text-slate-900 dark:text-white">{fmt(Math.round(d.value / 10000))}</span>
                                <span className="text-xs font-bold text-slate-400 w-8 text-right font-mono tabular-nums">{pct}%</span>
                              </div>
                            </div>
                            <div className="h-1 w-full bg-slate-100 dark:bg-white/[0.04] rounded-full overflow-hidden">
                              <div className="h-full rounded-full opacity-80" style={{ width: `${pct}%`, backgroundColor: d.color }} />
                            </div>
                          </li>
                        )
                      })}
                    </ul>
                    <button className="mt-4 text-xs font-bold text-blue-600 dark:text-blue-400 text-left hover:underline">
                      + 11 more categories
                    </button>
                  </article>
                </section>

                {/* 3. Project Pipeline Table */}
                <section className="rounded-2xl border border-slate-200/70 bg-white overflow-hidden dark:border-white/[0.06] dark:bg-slate-900/60 shadow-[0_1px_2px_rgba(15,23,42,0.04)] dark:shadow-none">
                  {/* Toolbar */}
                  <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 px-6 py-5 border-b border-slate-100 dark:border-white/[0.04]">
                    <div>
                      <h3 className="text-[15px] font-bold tracking-tight text-slate-900 dark:text-white">프로젝트 파이프라인</h3>
                      <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mt-1">디자인인사이트(주) · 진행중인 상업용 프로젝트 · 2026.4.20 기준</p>
                    </div>
                    <div className="flex items-center gap-2">
                       <div className="relative">
                         <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
                         <input type="text" placeholder="검색 (⌘K)" className="h-9 w-48 pl-9 pr-3 rounded-lg text-xs font-medium border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-slate-800/50 outline-none focus:ring-2 ring-blue-500 placeholder:text-slate-400" />
                       </div>
                       <button className="h-9 px-3 rounded-lg border border-slate-200 dark:border-white/10 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/5 flex items-center gap-1.5 transition-colors"><SlidersHorizontal className="w-3.5 h-3.5"/> 필터</button>
                    </div>
                  </header>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-[11px] font-bold uppercase tracking-wider text-slate-400 bg-slate-50/50 dark:bg-slate-800/20">
                          <th className="px-6 py-3 text-left">프로젝트</th>
                          <th className="px-4 py-3 text-left">상태</th>
                          <th className="px-4 py-3 text-right">계약금액</th>
                          <th className="px-4 py-3 text-right">입금</th>
                          <th className="px-4 py-3 text-right">미수금</th>
                          <th className="px-4 py-3 text-left w-40">진척</th>
                          <th className="px-4 py-3 text-left">담당</th>
                          <th className="px-4 py-3 text-right">제출일</th>
                          <th className="w-12" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-white/[0.04]">
                        {projLd ? (
                          <tr><td colSpan={9} className="text-center py-10 opacity-50"><div className="w-6 h-6 border-2 border-indigo-500/50 border-t-indigo-500 rounded-full animate-spin mx-auto"/></td></tr>
                        ) : projRes?.data?.items?.length === 0 ? (
                          <tr><td colSpan={9} className="text-center py-10 text-slate-500 text-sm">진행 중인 프로젝트가 없습니다.</td></tr>
                        ) : projRes?.data?.items?.map((r: any) => (
                          <tr key={r.projectId} onClick={() => setSelectedProject(r)}
                              className="group hover:bg-slate-50/80 dark:hover:bg-white/[0.02] transition-colors cursor-pointer">
                            <td className="px-6 py-4 space-y-1">
                              <div className="font-bold text-[13px] text-slate-900 dark:text-white tracking-tight">{r.name}</div>
                              <div className="text-[11px] font-semibold text-slate-400 font-mono tracking-wide">{r.projectNo || r.code} <span className="text-slate-300 dark:text-slate-600">·</span> {r.orderedBy || '상업'}</div>
                            </td>
                            <td className="px-4 py-4"><StatusBadge status={r.arStatus ?? 'QUOTE'} /></td>
                            <td className="px-4 py-4 text-right font-mono tabular-nums font-semibold text-slate-900 dark:text-slate-100">{fmt(r.contractAmount || 0)}</td>
                            <td className="px-4 py-4 text-right font-mono tabular-nums font-semibold text-emerald-600 dark:text-emerald-400">{fmt(r.paidAmount || 0)}</td>
                            <td className={`px-4 py-4 text-right font-mono tabular-nums font-bold ${(r.receivable || 0) > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-300 dark:text-slate-600'}`}>{fmt(r.receivable || 0)}</td>
                            <td className="px-4 py-4"><ProgressBar value={r.paidAmount || 0} total={r.contractAmount || 0} /></td>
                            <td className="px-4 py-4">
                              <div className="flex items-center gap-2">
                                <div className="w-5 h-5 rounded-full bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400 flex items-center justify-center text-[10px] font-black">{(r.pmName || 'A')[0]}</div>
                                <span className="text-[12px] font-semibold text-slate-700 dark:text-slate-300">{r.pmName || '미지정'}</span>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-right text-[11px] font-semibold text-slate-400 tabular-nums font-mono tracking-wide">{r.dueAt ? new Date(r.dueAt).toLocaleDateString() : 'N/A'}</td>
                            <td className="px-3 py-4 text-center"><ChevronRight className="w-4 h-4 inline-block text-slate-300 group-hover:text-slate-500 dark:text-slate-600 dark:group-hover:text-slate-400 transition" /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              </motion.div>
            )}

            {activeTab === 'finance' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-8">
                 <FinancePanel isDark={isDark} />
              </motion.div>
            )}

            {activeTab === 'insight' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-8 rounded-2xl border border-slate-200/70 bg-white overflow-hidden dark:border-white/[0.06] dark:bg-slate-900/60 shadow-[0_1px_2px_rgba(15,23,42,0.04)] dark:shadow-none hover:shadow-[0_8px_24px_-12px_rgba(15,23,42,0.18)] transition-all p-1">
                 <ProjectsInsightTracker onProjectClick={setSelectedProject} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Estimate Tab (Full Bleed View) */}
      {activeTab === 'estimate' && (
        <div className="w-full flex-1">
          {/* We keep the Header and Tabs outside the Estimate container so they are shared! Wait, earlier we wrapped the entire left section with px-4... */}
          <div className="px-6 lg:px-10 pt-6">
             <header className="mb-2 flex flex-col gap-4 text-slate-900 dark:text-white">
                <div>
                  <h1 className="text-2xl lg:text-3xl font-bold tracking-tight flex items-center gap-3">
                    <span aria-hidden="true">💳</span> SnapPay 파이프라인 관제
                  </h1>
                </div>
                {/* Underline Tabs (Duplicated for Full Bleed layout compatibility without heavy refactor) */}
                <div className="flex border-b border-slate-200 dark:border-white/10 gap-6 overflow-x-auto no-scrollbar">
                  {[
                    { id: 'estimate', label: '✨ AI 견적 엔진' },
                    { id: 'pipeline', label: '프로젝트 관제' },
                    { id: 'finance', label: '전사 재무 흐름' },
                    { id: 'insight', label: '미수금 관제' }
                  ].map(tab => (
                    <button key={tab.id} onClick={() => setActiveTab(tab.id as TabType)}
                      className={`pb-3 text-sm font-bold transition-all whitespace-nowrap relative ${
                        activeTab === tab.id ? 'text-blue-600 dark:text-blue-400' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
                      }`}
                    >
                      {tab.label}
                      {activeTab === tab.id && (
                        <motion.div layoutId="activeTabUnderline" className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400" />
                      )}
                    </button>
                  ))}
                </div>
             </header>
          </div>
          <SnapPayAIEstimateDemo />
        </div>
      )}

      {/* 4. Sliding Drawer for Project Details */}
      <AnimatePresence>
        {selectedProject && (
          <>
            <motion.div
              initial={{ backgroundColor: 'rgba(0,0,0,0)' }}
              animate={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
              exit={{ backgroundColor: 'rgba(0,0,0,0)' }}
              className="fixed inset-0 z-40 backdrop-blur-sm"
              onClick={() => setSelectedProject(null)}
            />
            <motion.aside
              initial={{ x: '100%', opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: '100%', opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed inset-y-0 right-0 w-full sm:max-w-[480px] z-50 bg-white dark:bg-slate-950 border-l border-slate-200 dark:border-white/[0.06] shadow-2xl flex flex-col"
            >
              <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 dark:border-white/[0.04]">
                <div>
                   <div className="flex items-center gap-2 mb-2">
                     <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[11px] font-mono font-bold tracking-tight">{selectedProject.code}</span>
                     {(selectedProject.receivable || 0) > 0 && selectedProject.aging?.daysOverdue > 0 && <span className="px-2 py-0.5 rounded-full bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 text-[11px] font-bold">D+{selectedProject.aging.daysOverdue} 연체중</span>}
                   </div>
                   <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">{selectedProject.name}</h2>
                </div>
                <button onClick={() => setSelectedProject(null)} className="p-2 -mt-6 rounded-lg text-slate-400 hover:text-slate-900 hover:bg-slate-50 dark:hover:text-white dark:hover:bg-white/5 transition-colors"><X className="w-5 h-5"/></button>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
                 {/* Financials Overview Hero */}
                 <div className="p-5 rounded-2xl bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-white/5 flex gap-4 mt-2">
                    <div className="flex-1">
                      <span className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">총 미수금 (남은 잔액)</span>
                      <span className="text-2xl font-black font-mono tabular-nums text-rose-600 dark:text-rose-400">{fmt(selectedProject.receivable || 0)}</span>
                    </div>
                    <div className="flex-1">
                      <span className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">총 계약 금액</span>
                      <span className="text-2xl font-black font-mono tabular-nums text-slate-700 dark:text-slate-300">{fmt(selectedProject.contractAmount || 0)}</span>
                    </div>
                 </div>

                 {/* 4 Tabs */}
                 <div className="flex border-b border-slate-200 dark:border-white/10 gap-6">
                   {[
                     { id: 'overview', label: '개요' },
                     { id: 'timeline', label: '청구·입금 타임라인' },
                     { id: 'communication', label: '커뮤니케이션 이력' },
                     { id: 'docs', label: '관련 문서' }
                   ].map(tab => (
                     <button key={tab.id} onClick={() => setDrawerTab(tab.id as any)}
                       className={`pb-3 text-[13px] font-bold transition-all relative ${
                         drawerTab === tab.id ? 'text-blue-600 dark:text-blue-400' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
                       }`}
                     >
                       {tab.label}
                       {drawerTab === tab.id && (
                         <motion.div layoutId="drawerTabUnderline" className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400" />
                       )}
                     </button>
                   ))}
                 </div>

                 {drawerTab === 'overview' && (
                   <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                     {/* Client Info */}
                     <div>
                       <h3 className="text-xs font-bold text-slate-900 dark:text-white flex items-center gap-2 mb-3"><UserIcon className="w-4 h-4"/> 책임 담당자 정보</h3>
                       <div className="space-y-2 text-[13px]">
                         <div className="flex items-center gap-2">
                           <span className="text-slate-500 w-24">발주처:</span>
                           <span className="font-bold text-slate-900 dark:text-white">{selectedProject.orderedBy || selectedProject.vendor?.name || '지정되지 않음'}</span>
                         </div>
                         <div className="flex items-center gap-2">
                           <span className="text-slate-500 w-24">사내 PM:</span>
                           <span className="font-medium text-slate-900 dark:text-white">{selectedProject.pmName || selectedProject.pm?.name || '미지정'}</span>
                         </div>
                         <div className="flex items-center gap-2">
                           <span className="text-slate-500 w-24">지급 기일(만기):</span>
                           <span className="font-mono font-medium text-slate-900 dark:text-white">{(selectedProject.nextDueAt || selectedProject.dueAt || selectedProject.dueDate) ? new Date(selectedProject.nextDueAt || selectedProject.dueAt || selectedProject.dueDate).toLocaleDateString() : 'N/A'}</span>
                         </div>
                       </div>
                     </div>

                     {/* AI Prediction Box */}
                     <div className="p-4 rounded-xl bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800/30">
                       <h4 className="text-[11px] font-bold text-blue-600 dark:text-blue-400 flex items-center gap-1.5 mb-2"><span aria-hidden="true">✨</span> AI 예측 및 추천</h4>
                       <p className="text-[13px] text-blue-800 dark:text-blue-200 leading-relaxed font-medium">
                         해당 발주처의 과거 대금 지급 주기를 분석한 결과, 강력한 독촉 메일(2차 경고) 발송 시 **평균 8.4일 이내**에 입금되는 패턴이 있습니다.<br/>
                         예상 입금일: <span className="underline font-bold decoration-blue-300 dark:decoration-blue-700 underline-offset-4">{(selectedProject.nextDueAt || selectedProject.dueAt || selectedProject.dueDate) ? new Date(new Date(selectedProject.nextDueAt || selectedProject.dueAt || selectedProject.dueDate).getTime() + 84 * 86400000).toLocaleDateString() : 'N/A'} 기준 + 84일 후 예상</span>
                       </p>
                     </div>
                   </motion.div>
                 )}

                 {drawerTab === 'timeline' && (
                   <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4 pb-4">
                     <div className="p-4 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-800">
                       <span className="text-[11px] font-bold text-slate-400 block mb-1">최근 업데이트</span>
                       <h4 className="text-[13px] font-bold text-slate-900 dark:text-white">청구서 발행 완료</h4>
                       <p className="text-xs font-medium text-slate-500 mt-1">계약금 및 중도금액 청구 (총 수주금액 확인됨)</p>
                     </div>
                     <div className="flex justify-center py-2">
                       <div className="w-px h-6 bg-slate-200 dark:bg-white/10"></div>
                     </div>
                     <div className="p-4 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-800 opacity-60">
                       <span className="text-[11px] font-bold text-slate-400 block mb-1">이전 내역</span>
                       <h4 className="text-[13px] font-bold text-slate-900 dark:text-white">견적 및 제안서 전송</h4>
                     </div>
                   </motion.div>
                 )}

                 {drawerTab === 'communication' && (
                   <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4 pb-4">
                      {selectedProject.mockEvents && selectedProject.mockEvents.length > 0 ? (
                        <div className="space-y-3">
                          {selectedProject.mockEvents.map((evt: any, i: number) => (
                             <div key={i} className="p-4 rounded-xl border border-blue-200 bg-blue-50 dark:border-blue-500/20 dark:bg-blue-900/10 flex items-start gap-3">
                               <div className="mt-0.5"><Mail className="w-4 h-4 text-blue-500"/></div>
                               <div>
                                 <h4 className="text-[13px] font-bold text-slate-900 dark:text-blue-100">{evt.title}</h4>
                                 <p className="text-[11px] text-slate-500 mt-1">{evt.desc}</p>
                                 <p className="text-[10px] text-slate-400 font-mono mt-2">{evt.time}</p>
                               </div>
                             </div>
                          ))}
                        </div>
                      ) : null}
                      {(!selectedProject.mockEvents || selectedProject.mockEvents.length === 0) && (
                        <div className="p-6 rounded-2xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-slate-900/50 flex flex-col items-center justify-center py-12">
                          <Mail className="w-8 h-8 text-slate-300 dark:text-slate-600 mb-3" />
                          <p className="text-[13px] font-bold text-slate-600 dark:text-slate-400">발송된 자동 독촉 메일이 없습니다.</p>
                          <p className="text-[11px] text-slate-400 mt-1">하단 액션 버튼을 통해 즉시 발송 가능합니다.</p>
                        </div>
                      )}
                   </motion.div>
                 )}

                 {drawerTab === 'docs' && (
                   <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3 pb-4">
                     <div className="p-4 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-800 flex items-center justify-between group cursor-pointer hover:border-blue-300 transition-colors">
                        <div className="flex items-center gap-3">
                           <div className="p-2 bg-blue-50 dark:bg-blue-500/10 rounded-lg text-blue-600 dark:text-blue-400">
                             <FileText className="w-5 h-5" />
                           </div>
                           <div>
                              <h4 className="text-[13px] font-bold text-slate-900 dark:text-white">{selectedProject.name || '프로젝트'}_납품계약서_최종.pdf</h4>
                              <span className="text-[11px] font-medium text-slate-400 font-mono mt-0.5 block">1.8MB</span>
                           </div>
                        </div>
                        <Download className="w-4 h-4 text-slate-400 group-hover:text-blue-500 transition-colors" />
                     </div>
                     <div className="p-4 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-800 flex items-center justify-between group cursor-pointer hover:border-blue-300 transition-colors">
                        <div className="flex items-center gap-3">
                           <div className="p-2 bg-rose-50 dark:bg-rose-500/10 rounded-lg text-rose-600 dark:text-rose-400">
                             <FileText className="w-5 h-5" />
                           </div>
                           <div>
                              <h4 className="text-[13px] font-bold text-slate-900 dark:text-white">{selectedProject.name || '프로젝트'}_비용청구서.pdf</h4>
                              <span className="text-[11px] font-medium text-slate-400 font-mono mt-0.5 block">840KB</span>
                           </div>
                        </div>
                        <Download className="w-4 h-4 text-slate-400 group-hover:text-blue-500 transition-colors" />
                     </div>
                   </motion.div>
                 )}
              </div>

              <div className="p-4 border-t border-slate-100 dark:border-white/[0.04] bg-slate-50/80 dark:bg-slate-900/50 flex items-center gap-3">
                 <button onClick={async () => {
                   const pId = selectedProject.projectId || selectedProject.id;
                   if (pId) {
                     showToast('📨 정중한 독촉 메일이 발송 예약되었습니다.');
                     const mockEvt = { title: '[시스템] 정중한 독촉 메일 1차 발송', desc: '해당 담당자 수신처로 이메일 발송이 예약되었습니다.', time: new Date().toLocaleString() };
                     setSelectedProject({...selectedProject, arStatus: 'dispute', mockEvents: [mockEvt, ...(selectedProject.mockEvents || [])]});
                     setDrawerTab('communication');
                     mutate((key: any) => typeof key === 'string' && key.startsWith('/api/pay-agent/projects'));
                   } else {
                     showToast('에러: 프로젝트 식별자가 없습니다.');
                   }
                 }} className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 font-bold text-[13px] shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition">
                   <Mail className="w-4 h-4"/> 정중한 독촉 생성
                 </button>
                 <button onClick={async () => {
                   const pId = selectedProject.projectId || selectedProject.id;
                   if (pId) {
                     showToast('🚨 내용증명 및 법무 검토안이 발송되었습니다.');
                     const mockEvt = { title: '[시스템] 2차 등기(내용증명) 발송', desc: '법무팀 검토 문서가 첨부된 내용증명이 발송 예약되었습니다.', time: new Date().toLocaleString() };
                     setSelectedProject({...selectedProject, arStatus: 'dispute', mockEvents: [mockEvt, ...(selectedProject.mockEvents || [])]});
                     setDrawerTab('communication');
                     mutate((key: any) => typeof key === 'string' && key.startsWith('/api/pay-agent/projects'));
                   } else {
                     showToast('에러: 프로젝트 식별자가 없습니다.');
                   }
                 }} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white font-bold text-[13px] shadow-sm hover:bg-blue-700 transition">
                   <AlertCircle className="w-4 h-4" /> 등기/법무 발송
                 </button>
                 <button onClick={async () => {
                   const pId = selectedProject.projectId || selectedProject.id;
                   if (pId) {
                     showToast('✅ 입금이 최종 확인되어 종결 처리되었습니다.');
                     const unpaidToPay = selectedProject.receivable ?? 0;
                     const mockEvt = { title: '[재무] 입금 결제 확인', desc: `잔액 ${unpaidToPay.toLocaleString()}원이 입금 확인 처리되었습니다.`, time: new Date().toLocaleString() };
                     setSelectedProject({...selectedProject, arStatus: 'paid_done', receivable: 0, paidAmount: (selectedProject.paidAmount || 0) + unpaidToPay, mockEvents: [mockEvt, ...(selectedProject.mockEvents || [])]});
                     setDrawerTab('communication');
                     mutate((key: any) => typeof key === 'string' && key.startsWith('/api/pay-agent/projects'));
                     mutate((key: any) => typeof key === 'string' && key.startsWith('/api/pay-agent/aggregations'));
                   } else {
                     showToast('에러: 프로젝트 식별자가 없습니다.');
                   }
                 }} className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-600 text-white font-bold text-[13px] shadow-sm hover:bg-emerald-700 transition">
                   <span>✓</span> 입금 확인
                 </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Global Mock Toast */}
      <AnimatePresence>
        {toastMsg && (
          <motion.div initial={{ opacity: 0, y: 50, x: '-50%' }} animate={{ opacity: 1, y: 0, x: '-50%' }} exit={{ opacity: 0, y: 20, x: '-50%' }} className="fixed bottom-10 left-1/2 z-[100] px-6 py-3 rounded-full bg-slate-900 border border-slate-700 text-white font-bold text-sm shadow-xl flex items-center gap-3">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />
              <span className="rounded-full h-2.5 w-2.5 bg-emerald-500" />
            </span>
            {toastMsg}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
